import { useState } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Dialog, Button, Divider } from '@mui/material'
import EmailIcon from '@mui/icons-material/MailOutline'
import DialogControl from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import CoreHeader from '../map/CoreHeader'
import CoreFooter from '../map/CoreFooter'
import useVerify, { Form, validate } from '../../hooks/verify/useVerify'

const BgImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_blue_dark.png`
const BgLoginImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_login_no_logo.png`
const LogoImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_main.png`

type VerifyProps = {}
const Verify = (props: VerifyProps) => {
    const {
        onSubmitForm,
        onClickOk,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        disabledEmail,
        disabledOTP,
        refCode,
    } = useVerify()
    const [openDialog, setOpenDialog] = useState(true)
    const classes = useStyles()
    const { t } = useTranslation()

    const validates = (values: Form) => {
        const validation = validate(values, t) || {}
        return validation
    }

    return (
        <>
            <Dialog
                fullScreen
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false)
                }}
                classes={{ paper: classes.drawerPaper }}
            >
                <CoreHeader />

                <StyledContainer>
                    <StyledBox>
                        <StyledBoxLeft>
                            <img src={LogoImage} className={classes.logo} />
                        </StyledBoxLeft>
                        <StyledBoxRight>
                            <StyledDivForm>
                                <StyledDivTitle>
                                    <StyledTypoTitle>{t('LABEL.VERIFY.TITLE')}</StyledTypoTitle>
                                    <Divider />
                                </StyledDivTitle>
                                <FinalForm onSubmit={onSubmitForm} validate={validates}>
                                    {({ handleSubmit, values, form }) => (
                                        <form onSubmit={handleSubmit}>
                                            <Grid container spacing={2} className={classes.container}>
                                                <Grid item xs={8}>
                                                    <Field
                                                        name="email"
                                                        label={t('LABEL.VERIFY.EMAIL')}
                                                        placeholder={t('LABEL.VERIFY.EMAIL')}
                                                        component={Textbox}
                                                        disabled={disabledEmail}
                                                        fullWidth
                                                        required
                                                        startAdornment={<IconEmail />}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            form.change('otp', '')
                                                            handleSubmit()
                                                        }}
                                                        classes={{ root: classes.buttonOTP }}
                                                    >
                                                        {`${t('BUTTON.SEND_OTP')}${!disabledOTP ? 'อีกครั้ง' : ''}`}
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Field
                                                        name="otp"
                                                        label={t('LABEL.VERIFY.OTP')}
                                                        placeholder={t('LABEL.VERIFY.OTP')}
                                                        textAfterLabel={refCode ? `(Ref. Code: ${refCode})` : undefined}
                                                        component={Textbox}
                                                        maxLength={6}
                                                        disabled={disabledOTP}
                                                        required={!disabledOTP ? true : false}
                                                        fullWidth
                                                    />
                                                    {!disabledOTP && (
                                                        <StyledTypoRemark>
                                                            <StyledLabelRemark>{'**'}</StyledLabelRemark>
                                                            {
                                                                ' หากไม่พบอีเมล กรุณาตรวจสอบเพิ่มเติมในถังขยะ (Junk) ที่อีเมลของท่าน'
                                                            }
                                                        </StyledTypoRemark>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} className={classes.divButtonGroup}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        type="submit"
                                                        disabled={
                                                            !disabledOTP && values.otp && values.otp?.length === 6
                                                                ? false
                                                                : true
                                                        }
                                                        classes={
                                                            !disabledOTP && values.otp && values.otp?.length === 6
                                                                ? { root: classes.buttonVerifyEnabled }
                                                                : { root: classes.buttonVerifyDisabled }
                                                        }
                                                    >
                                                        {t('BUTTON.VERIFY')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </form>
                                    )}
                                </FinalForm>
                            </StyledDivForm>
                        </StyledBoxRight>
                    </StyledBox>
                </StyledContainer>

                <StyledFooter>
                    <CoreFooter />
                </StyledFooter>
            </Dialog>
            <DialogControl
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <DialogControl
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default Verify

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        backgroundImage: `url(${BgImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divRememberForgot: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    checkbox: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        fontSize: '14px !important',
        marginLeft: '5px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        marginLeft: '5px !important',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'center',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    buttonOTP: {
        width: '100%',
        minHeight: '40px',
        fontSize: '14px !important',
        marginTop: '24px !important',
        boxShadow: `${theme.palette.getContrastText('shadow-bottom-blue')} !important`,
    },
    buttonVerifyEnabled: {
        width: '100%',
        minHeight: '37px',
        fontSize: '16px !important',
        boxShadow: `${theme.palette.getContrastText('shadow-bottom-blue')} !important`,
    },
    buttonVerifyDisabled: {
        width: '100%',
        minHeight: '37px',
        fontSize: '16px !important',
        color: `${theme.palette.text.primary} !important`,
        backgroundColor: `#E8E8E8 !important`,
        border: `#c7c7c7 solid 1px !important`,
    },
    logo: {
        height: 'auto',
        position: 'relative',
        width: 300,
        top: 40,
        [theme.breakpoints.down('lg')]: {
            width: 240,
            top: 20,
        },
    },
}))

const StyledFooter = styled('div')(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    width: '100%',
}))

const StyledContainer = styled('div')(({ theme }) => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 1120,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('lg')]: {
        width: 860,

        padding: '8px 64px',
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        marginLeft: 0,
        marginRight: 0,
        padding: '6px 32px',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '4px 20px',
    },
}))

const StyledBox = styled('div')(({ theme }) => ({
    width: '100%',
    height: '80%',
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '5px',
    boxShadow: theme.palette.getContrastText('shadow-box-blue'),
}))

const StyledBoxLeft = styled('div')(({ theme }) => ({
    width: 480,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    backgroundImage: `url(${BgLoginImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    borderRadius: '5px 0px 0px 5px',
    [theme.breakpoints.down('lg')]: {
        width: 360,
    },
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}))

const StyledBoxRight = styled('div')(({ theme }) => ({
    width: 640,
    height: '100%',
    borderRadius: '5px',
    backgroundColor: theme.palette.getContrastText('bg-login-right'),
    [theme.breakpoints.down('lg')]: {
        width: 540,
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        flexGrow: 1,
    },
    [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
    },
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    padding: '20px 50px',
    [theme.breakpoints.down('sm')]: {
        padding: '10px 30px',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    padding: '20px 0px',
}))

const StyledTypoTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    fontSize: '20px',
}))

const StyledTypoRemark = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: '12px',
    marginTop: '5px',
}))

const StyledLabelRemark = styled('label')(({ theme }) => ({
    color: '#d32f2f',
}))

const IconEmail = styled(EmailIcon)(({ theme }) => ({
    width: '20px',
    fill: theme.palette.primary.main,
}))
